import { Bounds, MapLike, Position, ValidGeoObject, isIGeofence, isNonNaNNumber, isPosition } from '@iotv/iotv-v3-types'
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon'
import { featureCollection, lineString, multiLineString, point, points, polygon, Units } from '@turf/helpers'
import { GeofenceType, GeofencePointsType } from '../../types/ModelTypes'
import { destination } from '@turf/destination'
import { circle } from '@turf/turf'
import { bbox } from '@turf/bbox'
import { center } from '@turf/center'
import { lineIntersect } from '@turf/line-intersect'
import { pointToLineDistance } from '@turf/point-to-line-distance'
import { nearestPointOnLine } from '@turf/nearest-point-on-line'
import { randomPoint, randomPosition } from '@turf/random'
import { objectsMatch } from '../objects/objectMatching'
import { IGeofence } from '../../ModelClassInterfaces/Geo'
import { existsFilter } from '@iotv/core-services'
import { distance } from '@turf/distance'
import { BBox, Feature, FeatureCollection, GeoJsonProperties, LineString, Point, Polygon, Position as TurfPosition } from 'geojson'

const debug = process.env.REACT_APP_DEBUG && false

export const convertGLatLngToPosition = (geofence: GeofenceType) => {
  const pointsArrayMap = geofence.points
  const pointsArrayArray: number[][] = []
  if (pointsArrayMap) pointsArrayMap.map((pointMap) => pointsArrayArray.push([pointMap.lat, pointMap.lng]))

  return pointsArrayArray
}

export const convertGPointsToTPosition = (points: GeofencePointsType[]): TurfPosition[] => {
  const pointsArrayArray: number[][] = []
  points.forEach((pointMap) => pointsArrayArray.push(gPointToTPosition(pointMap)))
  return pointsArrayArray
}

export const convertTPositionToGPoints = (points: TurfPosition[]): GeofencePointsType[] => {
  const pointsArrayArray: GeofencePointsType[] = []
  points.forEach((pointMap) => pointsArrayArray.push(tPositionToGPoint(pointMap)))
  return pointsArrayArray
}

/** for polygon wher first must equal last  */
export const gPointsToTClosedPosition = (points: Position[]): Position[] => {
  const clonedPts = [...points]
  const p0 = clonedPts[0]
  const pN = clonedPts[clonedPts.length - 1]
  if (!objectsMatch(p0, pN)) {
    clonedPts.push(p0)
  }
  return clonedPts
}

export const gPointToTPosition = (point: Position): TurfPosition => [point.lng, point.lat]

export const tPositionToGPoint = (position: TurfPosition) => convertTurfPointsToGLatLng(position)

export const convertTurfPointsToGLatLng = ([lng, lat]: TurfPosition): Position => {
  return { lat: lat, lng: lng }
}

//position must be [latNum, lonNum]
export const isInArea = (position: TurfPosition, geofence: IGeofence) => {
  debug && console.log('is in Area with: ', [position, geofence])
  let isInArea: boolean = false
  if (geofence === undefined || geofence.points === undefined) {
    console.log('Geofence undefined')
  } else {
    isInArea = tPositionIsInPoints(position, geofence.points)
  }

  return isInArea
}

export const gPositionIsInGPoints = (position: Position, points: Position[]) => {
  const tPosition = gPointToTPosition(position)
  return tPositionIsInPoints(tPosition, points)
}

export const tPositionIsInPoints = (position: TurfPosition, points: Position[]) => {
  const turfPoint = point(position)
  const turfPoly = positionArrayToTurfPolygon(points)
  const isInArea = booleanPointInPolygon(turfPoint, turfPoly)
  return isInArea
}

/* Defined in UI version of Turf Utils */
export const getThingInArea = (userView: MapLike<any>, pointArr: GeofencePointsType[] | undefined) => undefined
export const convertPointToTurfPosition = (position: GeofencePointsType | undefined) => undefined

export function isValidGPosition(gPos: any | undefined): gPos is Position {
  const keys = gPos ? Object.keys(gPos) : []
  return (
    keys.includes('lat') &&
    keys.includes('lng') &&
    typeof gPos?.lat === 'number' &&
    typeof gPos?.lng === 'number' &&
    gPos.lat >= -90 &&
    gPos.lat <= 90 &&
    gPos.lng >= -180 &&
    gPos.lng <= 180
  )
}

export function isValidGPositionSet(gPosArr: GeofencePointsType[]) {
  let clone: (GeofencePointsType | boolean)[] = [...gPosArr]
  let isValid: boolean = true
  clone.unshift(isValid)
  const testRes = clone.reduce((valid, next) => valid === true && isValidGPosition(next as GeofencePointsType))
  console.log(`Geofence test result is ${testRes} for`, gPosArr)
  return testRes
}

/** [sw, nw, ne, se] in {lat lng}s  */
export const turfBBoxToPoints = (bbx: BBox): Position[] => {
  const sw: Position = convertTurfPointsToGLatLng(bbx.slice(0, 2))
  const ne: Position = convertTurfPointsToGLatLng(bbx.slice(2, 4))
  const nw: Position = { lat: ne.lat, lng: sw.lng }
  const se: Position = { lat: sw.lat, lng: ne.lng }

  return [sw, nw, ne, se]
}

/** given a position and distance in meters to extend north easterly returns Google like Bounds */
export const extendPointToBounds = (pt: Position, metersDiag: number = 10): Bounds => {
  const pointA = point([pt.lng, pt.lat])
  const pointB = destination(pointA, metersDiag, 45, { units: 'meters' })
  const bbx = bbox(featureCollection([pointA, pointB]))
  const sw: Position = convertTurfPointsToGLatLng(bbx.slice(0, 2))
  const ne: Position = convertTurfPointsToGLatLng(bbx.slice(2, 4))
  return { sw, ne }
}

/** given a position and distance in meters to extend north easterly returns an set of points ( bbx ) [sw, nw, ne, se] */
export const extendPointToPoints = (pt: Position, metersDiag: number): Position[] => {
  const pointA = point([pt.lng, pt.lat])
  const pointB = destination(pointA, metersDiag, 45, { units: 'meters' })
  const bbx = bbox(featureCollection([pointA, pointB]))
  return turfBBoxToPoints(bbx)
}

export const getRandomPointInPoints = (points: Position[]): Position => {
  const tBounds = convertGPointsToTPosition(points)

  const tBBox = bbox(featureCollection(tBounds.map((tPos) => point(tPos))))
  const pt = randomPosition(tBBox)
  return convertTurfPointsToGLatLng(pt)
}

export const getRandomPointOutsidePoints = (points: Position[]): Position => {
  const getRandomPt = () => randomPoint(1).features[0].geometry.coordinates
  let outsidePt: TurfPosition | undefined = undefined
  while (!outsidePt || tPositionIsInPoints(outsidePt, points)) {
    outsidePt = getRandomPt()
  }
  return tPositionToGPoint(outsidePt)
}

const radiansToDegrees = (radians: number) => radians * (180 / Math.PI)
const degreesToRadians = (degrees: number) => (Math.PI / 180) * degrees
export const positionRadsToLatLng = ({ lat, lng }: { lat: number; lng: number }): Position => ({ lat: radiansToDegrees(lat), lng: radiansToDegrees(lng) })
export const latLngPositionToRads = ({ lat, lng }: Position): { lat: number; lng: number } => ({ lat: degreesToRadians(lat), lng: degreesToRadians(lng) })

type ExtendPositionToCirclishPolygonInput = { position: Position; steps: number; radialDistance: number; units?: Units; properties?: Record<string, any> }
/** polygon aroudn a Position ( { lat, lng } ), that can be a square when steps in 4, based on circle with centre position with radius radialDistance units, default units is meters */
export const extendPositionToCirclishPolygon = ({ position, steps, radialDistance, units = 'metres', properties }: ExtendPositionToCirclishPolygonInput) => {
  var center = [position.lng, position.lat]
  var radius = radialDistance
  var options = { steps, units, properties }
  return circle(center, radius, options)
}

export const turfPolygonToPositionArray = (turfPolygon: Polygon): Position[] => {
  const positionArray: Position[] = turfPolygon.coordinates[0].map(convertTurfPointsToGLatLng)
  return positionArray
}

export const positionArrayToTurfPolygon = (points: Position[]) => {
  const areaAsArrays: TurfPosition[] = convertGPointsToTPosition(gPointsToTClosedPosition(points))
  var turfPoly = polygon([areaAsArrays]) //seems to require array of arrays
  return turfPoly
}

export const positionArrayToTurfPolyline = (points: Position[]) => {
  const areaAsArrays: TurfPosition[] = convertGPointsToTPosition(gPointsToTClosedPosition(points))
  var turfLine = multiLineString([areaAsArrays]) //seems to require array of arrays
  return turfLine
}

export const geoObjectsToTurfFeatures = (geoObjects: ValidGeoObject[]) =>
  geoObjects
    .filter((g) => isPosition(g.location) || isIGeofence(g))
    .map((g) => {
      if (!!g.points) {
        return positionArrayToTurfPolygon(g.points)
      } else if (isPosition(g.location)) {
        return point(gPointToTPosition(g.location))
      } else {
        return null
      }
    })
    .filter(existsFilter) as (Feature<Point, GeoJsonProperties> | Feature<Polygon, GeoJsonProperties>)[]

export const geoObjectsToTurfPosition = (geoObjects: ValidGeoObject[]) =>
  geoObjects
    .filter((g) => isPosition(g.location) || isIGeofence(g))
    .map((g) => {
      if (g.points) {
        return g.points.map(gPointToTPosition)
      } else {
        return [gPointToTPosition(g.location!)]
      }
    })
    .flat()

/** returns bbox of position array, using turf bbox: extent in minX, minY, maxX, maxY order, which is sw, ne */
export const positionArrayToBBox = (positionArray: Position[]): Bounds => {
  const positionArrayTurf = convertGPointsToTPosition(positionArray)
  const tBBox = bbox(featureCollection(positionArrayTurf.map((tPos) => point(tPos))))
  const bboxIotv = { sw: { lat: tBBox[1], lng: tBBox[0] }, ne: { lat: tBBox[3], lng: tBBox[2] } }
  return bboxIotv
}

/** returns the centre IN TURF POSTITION of an IGeofence */
export const getIGeofenceCenterTurf = (geofence: IGeofence): TurfPosition => {
  const pArray: TurfPosition[] = geofence.points.filter(({ lat, lng }) => isNonNaNNumber(lat) && isNonNaNNumber(lng)).map(({ lat, lng }) => [lng, lat])
  const xCenter: TurfPosition = center(points(pArray)).geometry.coordinates
  return xCenter
}

type LineDistance = { turfLine: Feature<LineString, GeoJsonProperties>; distanceKm: number }

/** returns nearest TurfPosition as NearestPointOnLine ( a Feature ) on edges of polygon to given position - used to draw rays that dont converge on the position
 * If the position is not outside the polygon the returned value will be undefined, and consumers should use the original position
 */
export const getShortestIntersectTurfPosition = (position: Position, geofence: IGeofence) => {
  const tPos = gPointToTPosition(position)
  const areaAsArrays: TurfPosition[] = convertGPointsToTPosition(gPointsToTClosedPosition(geofence.points))
  const turfPoly: Feature<Polygon, GeoJsonProperties> = polygon([areaAsArrays]) //seems to require array of arrays
  const turfPolyLines = lineString(turfPoly.geometry.coordinates[0])
  // const geofenceEdges: TurfPosition[][] = turfPoly.geometry.coordinates[0].map( ( tPos, i) => [ tPos, turfPoly.geometry.coordinates[0][i + 1 ]]).filter( ( [ p1, p2 ]) => p1 && p2)
  // const turfLines: Feature<LineString, GeoJsonProperties>[] = geofenceEdges.map( ( edgeCoords ) =>  lineString( edgeCoords ))
  // const distanceToTurfLines: LineDistance[] = turfLines.map( ( line ) => ({ turfLine: line, distanceKm: pointToLineDistance( tPos, line, { units: 'kilometers'} )}))
  // const nearesLineDistance: LineDistance = distanceToTurfLines.reduce( ( a, c ) => c.distanceKm < a.distanceKm ? c : a  )
  const nearestPointOnline = nearestPointOnLine(turfPolyLines, tPos)
  return nearestPointOnline
}

export const getShortestIntersectPosition = (position: Position, geofence: IGeofence) => {
  const nearestPointOnLine = getShortestIntersectTurfPosition(position, geofence)
  const intersectPosition: Position = tPositionToGPoint(nearestPointOnLine.geometry.coordinates)
  return intersectPosition
}

export const getPerimerterIntersectTurfPosition = (position: Position, geofence: IGeofence) => {
  const tPos: TurfPosition = gPointToTPosition(position)
  const areaAsArrays: TurfPosition[] = convertGPointsToTPosition(gPointsToTClosedPosition(geofence.points))
  const turfPoly: Feature<Polygon, GeoJsonProperties> = polygon([areaAsArrays]) //seems to require array of arrays
  const geofenceCenter: Feature<Point, GeoJsonProperties> = center(turfPoly)
  const geofenceCenterTPosition: TurfPosition = geofenceCenter.geometry.coordinates
  const positionToGeofenceLine = lineString([tPos, geofenceCenterTPosition])
  const geofenceEdges: TurfPosition[][] = turfPoly.geometry.coordinates[0]
    .map((tPos, i) => [tPos, turfPoly.geometry.coordinates[0][i + 1]])
    .filter(([p1, p2]) => p1 && p2)
  const turfLines: Feature<LineString, GeoJsonProperties>[] = geofenceEdges.map((edgeCoords) => lineString(edgeCoords))
  const distanceToTurfLines: LineDistance[] = turfLines.map((line) => ({
    turfLine: line,
    distanceKm: pointToLineDistance(tPos, line, { units: 'kilometers' }),
  }))
  const nearesLineDistance: LineDistance = distanceToTurfLines.reduce((a, c) => (c.distanceKm < a.distanceKm ? c : a))
  const nearestEdge = nearesLineDistance.turfLine
  const intersectionPoint: FeatureCollection<Point, GeoJsonProperties> = lineIntersect(turfPoly, positionToGeofenceLine)

  const intersectionPointTurfPosition: TurfPosition | undefined = intersectionPoint.features?.[0]?.geometry?.coordinates
  const intersectPosition: Position | undefined = intersectionPointTurfPosition && tPositionToGPoint(intersectionPointTurfPosition)
  return intersectPosition
}

export const getGLineDistance = (start: Position, end: Position, units: Units) => {
  const tStart = gPointToTPosition(start)
  const tEnd = gPointToTPosition(end)
  const distanceBetweenPoints = distance(tStart, tEnd, { units })
  return distanceBetweenPoints
}
